import React, { useState } from 'react';
import useDarkMode from '../../hooks/useDarkMode';

/**
 * AuthLoadingScreen
 *
 * Lightweight loading screen for auth transitions. Must match the static HTML
 * placeholder exactly to avoid visual jumps during hydration.
 */
export function AuthLoadingScreen() {
  const isDark = useDarkMode();
  const [logoError, setLogoError] = useState(false);
  const isVerifying = (typeof window !== 'undefined' && sessionStorage.getItem('email_verification_in_progress') === 'true');
  
  const handleLogoError = () => {
    setLogoError(true);
  };
  
  const handleLogoLoad = (e) => {
    e.target.style.visibility = 'visible';
  };
  
  return (
    <div 
      className="fixed inset-0 z-50 flex flex-col items-center justify-start pt-48 md:justify-center md:pt-0 h-screen w-full bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800"
      style={{
        margin: 0,
        boxSizing: 'border-box',
        // Ensure this stays stable and doesn't interfere with any other elements
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0
      }}
    >
      {/* Single spinner container - exact match to HTML */}
      <div 
        className="relative"
        style={{
          margin: 0,
          padding: 0,
          position: 'relative'
        }}
      >
        {/* Stable spinner animation - exact match to HTML */}
        <div 
          className={`animate-spin rounded-full h-20 w-20 border-4 shadow-md ${
            isDark 
              ? 'border-gray-800 border-b-purple-400' 
              : 'border-gray-100 border-b-purple-600'
          }`}
          style={{
            margin: 0,
            padding: 0,
            position: 'relative',
            // Ensure smooth animation - same as HTML
            backfaceVisibility: 'hidden',
            transformStyle: 'preserve-3d'
          }}
        />
        
        {/* Logo container - exact match to HTML structure */}
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            margin: 0,
            padding: 0,
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '2.5rem',
            height: '2.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {/* Logo with Safari fallback - exact match to HTML */}
          {!logoError && (
            <img 
              src="/images/loading.png" 
              alt="Logo" 
              className="w-10 h-10 opacity-90"
              onError={handleLogoError}
              onLoad={handleLogoLoad}
              style={{
                margin: 0,
                padding: 0,
                display: 'block',
                visibility: 'hidden',
                width: '2.5rem',
                height: '2.5rem',
                opacity: 0.9,
                // Safari optimizations
                WebkitBackfaceVisibility: 'hidden',
                backfaceVisibility: 'hidden'
              }}
            />
          )}
          
          {/* Fallback dot - exact match to HTML */}
          {logoError && (
            <div 
              style={{
                width: '1rem',
                height: '1rem',
                backgroundColor: isDark ? '#a855f7' : '#9333ea',
                borderRadius: '50%',
                opacity: 0.8,
                display: 'block'
              }}
            />
          )}
        </div>
      </div>
      {/* Contextual message (shown only during email verification) */}
      {isVerifying && (
        <div 
          className="mt-8 px-4 py-2 rounded-full bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 text-purple-700 dark:text-purple-300 text-sm shadow-sm"
          role="status"
          aria-live="polite"
        >
          Verifying your account...
        </div>
      )}
    </div>
  );
}

export default AuthLoadingScreen; 